import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme/index'
import { Link } from 'gatsby'
import React, { memo, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import Fade from 'react-reveal/Fade'

export interface Props {
  childs?: ChildsProps[]
  className?: string
  description?: string
  id?: string
  index?: number
  isRooms?: boolean
  isVisible?: boolean
  sort?: number
  title: string
  languageCode?: string
  navigationStatus?: boolean
  url: string
}

interface ChildsProps {
  id?: string
  isVisible?: boolean
  sort?: number
  title: string
  url: string
  weight?: number
}

export const MenuItem = memo(function MenuItem({
  childs,
  className,
  description,
  index,
  isRooms,
  navigationStatus,
  title,
  url,
}: Props) {
  if (!title) {
    return null
  }

  const [submenuVisibility, setSubmenuVisibility] = useState(false)

  useEffect(() => {
    setSubmenuVisibility(false)
  }, [])

  return (
    <>
      {url ? (
        <Fade
          left
          delay={index ? index * 150 : 0}
          distance="5.625rem"
          speed={600}
          when={navigationStatus}
        >
          <Anchor className={className} to={url} activeClassName="active">
            <Label>{title}</Label>
          </Anchor>
        </Fade>
      ) : (
        <Fade
          left
          delay={index ? index * 150 : 0}
          distance="5.625rem"
          speed={600}
          when={navigationStatus}
        >
          <NoLink
            dial={4}
            className={submenuVisibility ? 'open' : undefined}
            inline
            onClick={() =>
              submenuVisibility
                ? setSubmenuVisibility(false)
                : setSubmenuVisibility(true)
            }
            row
            tag="span"
          >
            <Label>{title}</Label>
            {submenuVisibility ? <ChevronUp /> : <ChevronDown />}
          </NoLink>
        </Fade>
      )}
      {childs && childs.length > 0 ? (
        <Wrapper className={submenuVisibility ? 'open' : undefined}>
          {description ? <Description>{description}</Description> : null}
          <Submenu className={isRooms ? 'rooms-submenu' : undefined}>
            {childs
              .sort((a: any, b: any) => a.weight - b.weight)
              .map((item, index) => (
                <ChildItem key={index} {...item} />
              ))}
          </Submenu>
        </Wrapper>
      ) : null}
    </>
  )
})

export const ChildItem = memo(function ChildItem({ title, url }: ChildsProps) {
  return (
    <SubmenuLi>
      <Anchor to={url} activeClassName="active">
        {title}
      </Anchor>
    </SubmenuLi>
  )
})

const Style = css`
  color: ${theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.heading};
  font-size: 4.5vh;
  line-height: 5.3vh;
  transition: 0.2s ease-out;

  @media (max-width: 1199px) {
    font-size: 3.5vh;
    line-height: 4vh;
  }
`

const Anchor = styled(Link)`
  &:hover,
  &.active {
    color: ${theme.colors.variants.primaryLight1};
  }

  ${Style}
`

const NoLink = styled(FlexBox)`
  cursor: pointer;

  svg {
    width: auto;
    height: 2.6vh;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    margin-left: 1rem;
    transform: translateY(0.8vh);
  }

  ${Style}
`

const Label = styled.span``

const Wrapper = styled.div`
  max-height: 0;
  overflow: hidden;
  transition: 0;
  &.open {
    max-height: 250px;
    margin-top: 0.75rem;
    transition: max-height 0.6s;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryLight1};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-style: italic;
  line-height: 1.75rem;
  margin-bottom: 1.3125rem;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Submenu = styled.ul`
  overflow: hidden;
  &.rooms-submenu {
    li {
      width: 33.333%;
      margin-top: 0.625rem;
      &:first-of-type {
        width: 100%;
        margin-bottom: 6px;
        a {
          font-size: 1.6vh;
          font-weight: 500;
          letter-spacing: 0.0437rem;
          line-height: 1.7vh;
          text-transform: uppercase;
        }
      }
      a {
        font-size: 1.8vh;
        font-weight: 400;
        letter-spacing: normal;
        line-height: 3.1vh;
        text-transform: inherit;
      }
    }
  }

  li {
    width: 50%;
    float: left;
    margin-top: 1.0625rem;
  }

  @media (max-width: 1199px) {
    &.rooms-submenu {
      li {
        width: 100%;
      }
    }
    li {
      width: 100%;
      float: none;
    }
  }
`

const SubmenuLi = styled.li`
  a {
    font-size: 1.6vh;
    font-weight: 500;
    letter-spacing: 0.0437rem;
    line-height: 1.7vh;
    text-transform: uppercase;
  }
`
